import type { NotificationResult } from '@sharedlib/notification-contract';

export type Unsubscribe = () => void;

/** Locally adapted candidate, not a backend authorization or transport contract. */
export interface NotificationCandidate {
  readonly version: 1;
  readonly id: string;
  readonly scopeId: string;
  readonly dedupeId: string;
  readonly source: string;
  readonly category: string;
  readonly expiresAt: number;
  readonly payload: unknown;
}

export interface NotificationPresentation {
  readonly title: string;
  readonly body: string;
  readonly silent: boolean;
  /** Optional opaque subject key for the same usable view; never a route or URL. */
  readonly viewKey?: string;
}

export interface NotificationOperation {
  readonly signal: AbortSignal;
  readonly identityGeneration: string;
}

export interface NotificationPlugin {
  readonly source: string;
  readonly category: string;
  readonly accepts: (candidate: NotificationCandidate) => boolean;
  readonly format: (
    candidate: NotificationCandidate,
    operation: NotificationOperation,
  ) => NotificationPresentation | Promise<NotificationPresentation>;
  /** Must revalidate authorization and respect signal immediately before navigation. */
  readonly activate: (candidate: NotificationCandidate, operation: NotificationOperation) => void | Promise<void>;
}

export interface NotificationSource {
  /** Sources emit only fresh events; recovery/backfill is deliberately not a notification candidate. */
  readonly subscribe: (listener: (candidate: unknown) => void, signal: AbortSignal) => Unsubscribe;
}

export interface NotificationRegistration {
  readonly source: NotificationSource;
  readonly plugin: NotificationPlugin;
}

export interface NotificationIdentity {
  readonly generation: string;
  /** UI isolation labels supplied by the authenticated host; not proof of server access. */
  readonly scopeIds: readonly string[];
}

export interface NotificationView {
  readonly scopeId: string;
  readonly key: string;
  /** Host combines panel usability and document visibility; main owns final native attention. */
  readonly readable: boolean;
}

export interface PresentationCommand {
  readonly candidate: NotificationCandidate;
  readonly presentation: NotificationPresentation;
  readonly contextRevision: number;
  readonly suppressIfAttentive: boolean;
}

export interface PresenterActivation {
  readonly id: string;
  readonly eventId: string;
}

export interface NotificationSession {
  readonly show: (command: PresentationCommand) => Promise<NotificationResult>;
  readonly updateContext: (revision: number) => Promise<void>;
  readonly onActivated: (listener: (event: PresenterActivation) => void) => Unsubscribe;
  readonly acknowledge: (eventId: string) => Promise<void>;
  readonly dismiss: (ids: readonly string[]) => Promise<void>;
  readonly clearScope: (scopeId: string) => Promise<void>;
  readonly close: () => Promise<void>;
}

export interface NotificationPresenter {
  /** null means unavailable, not OS permission denial. Must not prompt browser permissions. */
  readonly open: () => Promise<NotificationSession | null>;
}

export interface NotificationPolicy {
  /** Missing/unloaded/failed policy must deny; formatter cannot widen backend permission. */
  readonly allows: (candidate: NotificationCandidate, operation: NotificationOperation) => boolean | Promise<boolean>;
}

export interface TypedNotificationPlugin<Payload> {
  readonly source: string;
  readonly category: string;
  readonly isPayload: (value: unknown) => value is Payload;
  readonly format: (
    payload: Payload,
    operation: NotificationOperation,
  ) => NotificationPresentation | Promise<NotificationPresentation>;
  readonly activate: (payload: Payload, operation: NotificationOperation) => void | Promise<void>;
}

/** Explicit typed adapter registration; never discovers plugins or starts a source. */
export function defineNotificationPlugin<Payload>(definition: TypedNotificationPlugin<Payload>): NotificationPlugin {
  return {
    source: definition.source,
    category: definition.category,
    accepts: (candidate): boolean => definition.isPayload(candidate.payload),
    format: (candidate, operation): NotificationPresentation | Promise<NotificationPresentation> => {
      if (!definition.isPayload(candidate.payload)) throw new Error('Invalid notification payload.');
      return definition.format(candidate.payload, operation);
    },
    activate: (candidate, operation): void | Promise<void> => {
      if (!definition.isPayload(candidate.payload)) throw new Error('Invalid notification payload.');
      return definition.activate(candidate.payload, operation);
    },
  };
}
